
/**
 * First we will load all of this project's JavaScript dependencies which
 * includes Vue and other libraries. It is a great starting point when
 * building robust, powerful web applications using Vue and Laravel.
 */
import "./bootstrap";
import router from "./routes";
import VueEvents from "vue-events";
import Vue from "vue";
import Vuex from "vuex";
import VueCurrencyInput from "vue-currency-input";
import App from "./layout/App.vue";
import Antd from 'ant-design-vue';
import 'ant-design-vue/dist/antd.css';
import '@/shared/styles/antd-overrides.less';
import { setUserData } from "./utils";
import { isOnPasswordRecoveryPath } from "./shared/lib/forceSessionExpired";
import { normalizeRecoveryUrl } from "./shared/lib/normalizeRecoveryUrl";
import { DeviceSizeEnum } from "./enums/GeneralEnums";
import itineraries from '@/modules/Itineraries/store';
import freightTables from '@/modules/FreightTables/store';
import Clarity from "@microsoft/clarity";
import {
  installSensitiveFieldMask,
  startClarity,
  trackClarityScreen,
} from "./shared/lib/clarityTracking";

Vue.use(Vuex);
Vue.use(Antd);
const savedBreadcrumb = JSON.parse(localStorage.getItem('breadcrumb'));
const store = new Vuex.Store({
    modules: {
        itineraries,
        freightTables,
    },
    state: {
        device_size: DeviceSizeEnum.MD,
        is_full_screen: false,
        isloaded: false,
        is_user_data_loaded: false,
        casas_decimais: 0,
        multi_empresa: false,
        cadastro_inicial: false,
        introducao: false,
        certificado_instalado: false,
        unidade_active: {
            cd_unidade: 'Sem unidade',
            descr_unidade: 'Favor solicitar acesso a uma unidade',
            utiliza_modulo_conferencia: false,
            aviso_vencimento: false,
            dias_vencimento: 0,
        },
        bplano: {
            qtde_unidade: 'Favor entrar em contato com o suporte',
            qtde_cte: 'Favor entrar em contato com o suporte',
            qtde_mdfe: 'Favor entrar em contato com o suporte',
            is_free: 'Favor entrar em contato com o suporte',
            descr: 'Favor entrar em contato com o suporte'
        },
        bcliente_active: {cd_bcliente: '', nome_bcliente: ''},
        api_version: '',
        logged: { user: null, permissions: [], bclientes: [], notificacao: [] },
        unidades: [],
        notificacao: [],
        app: {isVisibleSubMenu: false},
        vuetable: {filterRow: true, filterButton: false},
        breadcrumb: savedBreadcrumb,
        currency: {
            hideNegligibleDecimalDigits: false,
            hideCurrencySymbol: false,
            hideGroupingSymbol: false,
            locale: "de-DE",
            currency: "BRL",
            position: "prefix"
        },
    },
    getters: {
        device_size: state => state.device_size,
        logged: state => state.logged,
        unidades: state => state.unidades,
        notificacao: state => state.notificacao,
        vuetable: state => state.vuetable,
        app: state => state.app,
        unidade_active: state => state.unidade_active,
        bplano: state => state.bplano,
        bcliente_active: state => state.bcliente_active,
        api_version: state => state.api_version,
        isloaded: state => state.isloaded,
        is_user_data_loaded: (state) => state.is_user_data_loaded,
        is_full_screen: state => state.is_full_screen,
        multi_empresa: state => state.multi_empresa,
        casas_decimais: state => state.casas_decimais,
        cadastro_inicial: state => state.cadastro_inicial,
        introducao: state => state.introducao,
        certificado_instalado: state => state.certificado_instalado,
        breadcrumb: state => state.breadcrumb,
        currency: state => state.currency
    },
    mutations: {
        setDeviceSize: function (state, width) {
            if (width < 576) state.device_size = DeviceSizeEnum.XS;
            else if (width < 768) state.device_size = DeviceSizeEnum.SM;
            else if (width < 992) state.device_size = DeviceSizeEnum.MD;
            else if (width < 1200) state.device_size = DeviceSizeEnum.LG;
            else state.device_size = DeviceSizeEnum.XL;
        },
        setUser: function (state, payload) {
            state.logged.user = payload.user;
            state.logged.permissions = payload.permissions;
            state.logged.bclientes = payload.bclientes;
            state.logged.notificacao = payload.notificacao;
            state.multi_empresa = payload.user.bcliente_active.multi_empresa;
            state.cadastro_inicial = payload.user.bcliente_active.cadastro_inicial;
            state.introducao = payload.user.bcliente_active.introducao;
            state.nome_sistema = payload.user.bcliente_active.b_config_blogistic.nome_sistema;
            state.casas_decimais = payload.user.bcliente_active.b_config_blogistic.casas_decimais;
            state.unidades = null;
            if (payload.user.bcliente_active.b_config_blogistic.path_certificado_a1 !== null
                || payload.user.bcliente_active.b_config_blogistic.tp_integracao_sefaz == 2) {
                state.certificado_instalado = true;
            }
            if (payload.unidades != null) {
                state.unidades = payload.unidades;
            }
            state.isloaded = true;
            state.is_user_data_loaded = true;
        },
        BPlano: function (state, payload) {
            if (payload != null) {
                state.bplano.descr = payload.b_plano.descr + "| Qtde. CTe:" + payload.b_plano.qtde_cte + "| Qtde. MDFe:" + payload.b_plano.qtde_mdfe + "| Qtde. Unidade:" + payload.b_plano.qtde_unidade;
                state.bplano.qtde_cte = payload.qtde_cte;
                state.bplano.qtde_mdfe = payload.qtde_mdfe;
                state.bplano.qtde_unidade = payload.qtde_unidade;
                state.bplano.is_free = payload.is_free;
            }
        },
        setUnidade: function (state, payload) {
            if (payload != null) {
                state.unidade_active.descr_unidade = payload.descr;
                state.unidade_active.cd_unidade = payload.cd;
                state.unidade_active.utiliza_modulo_conferencia = payload.utiliza_modulo_conferencia === 1;
                state.unidade_active.aviso_vencimento = payload.aviso_vencimento === 1;
                state.unidade_active.dias_vencimento = payload.dias_vencimento;
            }
        },
        setBcliente: function (state, payload) {
            state.bcliente_active.cd_bcliente = payload.cd;
            state.bcliente_active.nome_bcliente = payload.nome;
        },
        setBreadcrumb: function (state, payload) {
            state.breadcrumb = payload;
            localStorage.setItem('breadcrumb', JSON.stringify(payload));
        },
        setApiVersion: function (state, payload) {
            state.api_version = payload;
        },
        setVuetableFilterButton: function (state, payload) {
            state.vuetable.filterButton = payload;
        },
        setVuetableFilterRow: function (state, payload) {
            state.vuetable.filterRow = payload;
        },
        setIsFullScreen: function (state, payload) {
            state.is_full_screen = !state.is_full_screen;
        },
        setMenuShowHide: function (state, isVisibleSubMenu) {
            state.app.isVisibleSubMenu = !isVisibleSubMenu;
        },
    },
});

Vue.mixin({
    methods: {
        getPermissionFw: function (permissionCheck) {
            // `store`, e não `app.$store`: no reload a rota inicial já resolve dentro do `new Vue`
            // e a tela chama isto no primeiro render, antes de `app` existir.
            var permissions = store.state.logged.permissions || [];
            return permissions
                .some(permission => permission.name === permissionCheck);
        },
        expandFullscreenFw() {
            if (this.is_full_screen) {
                this.$store.commit('setIsFullScreen', false);
            } else {
                this.$store.commit('setIsFullScreen', true);
            }
        },
        onFilterShowHideFw() {
            if (this.vuetableFilterRow) {
                this.$store.commit('setVuetableFilterRow', false);
            } else {
                this.$store.commit('setVuetableFilterRow', true);
            }
        },
        setSubMenuShowHideFw(menuBool) {
            this.$store.commit('setMenuShowHide', menuBool);
            return this.$store.getters.app.isVisibleSubMenu;
        },
        getSubMenuShowHideFw() {
            return this.$store.getters.app.isVisibleSubMenu;
        },
    }
});

function updateDeviceSize() {
    store.commit("setDeviceSize", window.innerWidth);
}

updateDeviceSize();

window.addEventListener('resize', updateDeviceSize);

// Corrige a URL do link de e-mail (sem hash / `#` escapado) antes do router
// iniciar a navegacao, que acontece no `beforeCreate` da instancia raiz.
normalizeRecoveryUrl();

/**
 * Next, we will create a fresh Vue application instance and attach it to
 * the page. Then, you may begin adding components to this application
 * or customize the JavaScript scaffolding to fit your unique needs.
 */
const clarityStarted = startClarity(
  Clarity,
  {
    VUE_APP_CLARITY_PROJECT_ID: process.env.VUE_APP_CLARITY_PROJECT_ID,
  },
  isOnPasswordRecoveryPath()
);
installSensitiveFieldMask(typeof document === "undefined" ? null : document);

const app = new Vue({
  el: "#app",
  router,
  VueEvents,
  store,
  render: (h) => h(App),
  async created() {
    // Recuperacao de senha roda sem sessao: buscar o usuario ali retorna 401 e
    // derruba a pagina para o login antes do usuario trocar a senha.
    // Le o hash direto: no created a rota inicial pode ainda ser START.
    if (isOnPasswordRecoveryPath()) {
      return;
    }
    await setUserData(this.$store);
  },
});

function publishClarityScreen(to) {
  if (clarityStarted) {
    trackClarityScreen(Clarity, to);
  }
}

router.afterEach((to) => {
  publishClarityScreen(to);
  Vue.nextTick(() => {
    installSensitiveFieldMask(typeof document === "undefined" ? null : document);
  });
});

// A primeira navegação pode terminar dentro do `new Vue`, antes deste hook.
publishClarityScreen(router.currentRoute);

router.beforeEach((to, from, next) => {
    updatedBreadcrumb(to, store);

    if (!to.meta.requiresAuth) {
        next();
        return;
    }

    const logged = app.$store.state.logged || {};
    const user = logged.user;
    if (!user) {
        next('/');
        return;
    }

    const stateLoggerPermissions = logged.permissions || [];
    const hasPermission = stateLoggerPermissions.some(
        (permission) => permission.name === to.name
    );

    if (hasPermission) {
        next();
        return;
    }

    const permission = new window.Form({route: to.name});
    permission.post('/api/usuario/checkPermission')
        .then(response => {
            if (response.permission) {
                next();
                return;
            }
            var obj = {
                isShown: true,
                title: response.title,
                message: response.message,
                type: response.tp_message,
                customCloseBtnText: response.alert_obj.customCloseBtnText,
                customConfirmBtnText: response.alert_obj.customConfirmBtnText,
                useConfirmBtn: response.alert_obj.useConfirmBtn,
                onClose: this.onClose,
            }
            app.$Simplert.open(obj);
            next(false);
        })
        .catch(() => {
            next('/');
        });
})

export function updatedBreadcrumb(to, store) {
    const breadcrumb = {
        titulo: to.meta.titulo || 'Dashboard',
        p1: to.meta.p1,
        p2: to.meta.p2,
        p1Path: to.meta.p1Path
    };
    store.commit('setBreadcrumb', breadcrumb);
}

export const apiVersion = () => {
  return store.getters.api_version ? store.getters.api_version : 1;
};
